import { drawPixelText, textWidth } from './font';
import { hash2 } from './px';
import { shade } from './sprites';
import { BUILDINGS, buildGround, DECOR, MAP_H, MAP_W, TILE, type Building, type Decor, type Ground } from './world';

/** Отрисовка карты: статичный слой (земля и дома) один раз в offscreen canvas, предметы — каждый кадр по y. */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface StaticLayer {
  canvas: HTMLCanvasElement;
  /** Окна для ночи: часть загорается тёплым светом. */
  windows: (Rect & { warm: boolean })[];
  ground: Ground[][];
}

const C = {
  grass: '#63b354',
  grassDark: '#559e48',
  grassLight: '#7cc76a',
  pave: '#cfcac0',
  paveLine: '#bdb7ab',
  road: '#565a63',
  roadLight: '#60646d',
  roadDark: '#4d5059',
  curb: '#8e8f96',
  mark: '#f2efe6',
  water: '#3f86d9',
  waterDeep: '#3577c6',
  wave: '#86bdf0',
  sand: '#dcc995',
  sandDark: '#c9b47e',
  rail: '#8c877d',
  sleeper: '#5b4636',
  steel: '#c7c9cf',
  hedge: '#2f7a3a',
  hedgeLight: '#3f8f48',
  hedgeDark: '#245e2d',
  bridge: '#bdb6a6',
  wall: '#7d776c',
  glass: '#9fc6e6',
  glassDark: '#6b97bd',
  frame: '#f4f1ea',
  door: '#5a3b2a',
  ink: '#1b1a26',
};

function px(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string) {
  ctx.fillStyle = c;
  ctx.fillRect(x, y, w, h);
}

// ---------------------------------------------------------------------------------------------- земля
function drawTile(ctx: CanvasRenderingContext2D, g: Ground[][], tx: number, ty: number) {
  const t = g[ty][tx];
  const X = tx * TILE;
  const Y = ty * TILE;
  const base: Record<Ground, string> = {
    grass: C.grass,
    pave: C.pave,
    road: C.road,
    water: ty >= 26 ? C.waterDeep : C.water,
    rail: C.rail,
    sand: C.sand,
    bridge: C.bridge,
    hedge: C.hedge,
  };
  px(ctx, X, Y, TILE, TILE, base[t]);
  for (let j = 0; j < TILE; j++) {
    for (let i = 0; i < TILE; i++) {
      const r = hash2(X + i, Y + j, 7);
      if (t === 'grass') {
        if (r < 0.05) px(ctx, X + i, Y + j, 1, 1, C.grassDark);
        else if (r > 0.985) px(ctx, X + i, Y + j, 1, 1, C.grassLight);
      } else if (t === 'road') {
        if (r < 0.04) px(ctx, X + i, Y + j, 1, 1, C.roadDark);
        else if (r > 0.97) px(ctx, X + i, Y + j, 1, 1, C.roadLight);
      } else if (t === 'sand' && r < 0.05) px(ctx, X + i, Y + j, 1, 1, C.sandDark);
      else if (t === 'rail' && r < 0.12) px(ctx, X + i, Y + j, 1, 1, shade(C.rail, r < 0.06 ? -0.15 : 0.12));
    }
  }
  if (t === 'grass' && hash2(tx, ty, 3) > 0.6) {
    const gx = X + 3 + Math.floor(hash2(tx, ty, 4) * 9);
    const gy = Y + 4 + Math.floor(hash2(tx, ty, 5) * 8);
    px(ctx, gx, gy, 1, 2, C.grassDark);
    px(ctx, gx + 2, gy, 1, 2, C.grassDark);
    px(ctx, gx + 1, gy + 1, 1, 2, C.grassDark);
  }
  if (t === 'pave' || t === 'bridge') {
    const line = t === 'pave' ? C.paveLine : shade(C.bridge, -0.12);
    px(ctx, X, Y + 7, TILE, 1, line);
    px(ctx, X, Y + 15, TILE, 1, line);
    px(ctx, X + ((ty % 2) ? 3 : 11), Y, 1, 7, line);
    px(ctx, X + ((ty % 2) ? 11 : 3), Y + 8, 1, 7, line);
  }
  if (t === 'hedge') {
    for (let i = 0; i < TILE; i += 4) {
      px(ctx, X + i + 1, Y + 2, 3, 3, C.hedgeLight);
      px(ctx, X + i, Y + 11, 4, 5, C.hedgeDark);
    }
  }
  if (t === 'rail') {
    for (let i = 1; i < TILE; i += 4) px(ctx, X + i, Y + 2, 2, 12, C.sleeper);
    px(ctx, X, Y + 4, TILE, 1, C.steel);
    px(ctx, X, Y + 5, TILE, 1, shade(C.rail, -0.35));
    px(ctx, X, Y + 11, TILE, 1, C.steel);
    px(ctx, X, Y + 12, TILE, 1, shade(C.rail, -0.35));
  }
  if (t === 'water') {
    const above = g[ty - 1]?.[tx];
    if (above && above !== 'water' && above !== 'bridge') {
      px(ctx, X, Y, TILE, 3, '#9aa3ad');
      px(ctx, X, Y + 3, TILE, 1, '#6f7882');
    }
    for (let k = 0; k < 2; k++) {
      const wx = X + Math.floor(hash2(tx, ty, 11 + k) * 12);
      const wy = Y + 5 + Math.floor(hash2(tx, ty, 21 + k) * 9);
      px(ctx, wx, wy, 3, 1, shade(base.water, 0.2));
    }
  }
  if (t === 'road') {
    if (ty === 10) px(ctx, X, Y, TILE, 1, C.curb);
    if (ty === 11) {
      px(ctx, X, Y + 15, TILE, 1, C.curb);
      // разметка по середине дороги — между рядами 10 и 11
      if (tx % 2 === 0) px(ctx, X + 2, Y - 1, 10, 2, C.mark);
    }
    // «зебра» у улицы
    if (tx === 17 || tx === 18) for (let i = 1; i < TILE; i += 4) px(ctx, X + i, Y + 1, 2, 14, C.mark);
  }
  if (t === 'bridge' && (tx === 26 || tx === 28)) {
    px(ctx, tx === 26 ? X : X + 13, Y, 3, TILE, C.wall);
    px(ctx, tx === 26 ? X : X + 13, Y, 3, 1, shade(C.wall, 0.25));
  }
}

// ---------------------------------------------------------------------------------------------- дома
function brickWall(ctx: CanvasRenderingContext2D, X: number, Y: number, W: number, H: number, wall: string) {
  px(ctx, X, Y, W, H, wall);
  const mortar = shade(wall, 0.18);
  for (let j = 0; j < H; j += 4) {
    px(ctx, X, Y + j + 3, W, 1, mortar);
    for (let i = (j / 4) % 2 ? 0 : 4; i < W; i += 8) px(ctx, X + i, Y + j, 1, 3, mortar);
  }
}

function stoneWall(ctx: CanvasRenderingContext2D, X: number, Y: number, W: number, H: number, wall: string) {
  px(ctx, X, Y, W, H, wall);
  const line = shade(wall, -0.12);
  for (let j = 0; j < H; j += 6) {
    px(ctx, X, Y + j + 5, W, 1, line);
    for (let i = (j / 6) % 2 ? 2 : 8; i < W; i += 12) px(ctx, X + i, Y + j, 1, 5, line);
  }
}

function roofTiles(ctx: CanvasRenderingContext2D, X: number, Y: number, W: number, H: number, roof: string) {
  px(ctx, X, Y, W, H, roof);
  px(ctx, X, Y, W, 2, shade(roof, 0.22));
  for (let j = 5; j < H - 3; j += 4) px(ctx, X + 1, Y + j, W - 2, 1, shade(roof, -0.12));
  px(ctx, X, Y + H - 3, W, 3, shade(roof, -0.32));
}

/** Вывеска текущего дома: окна под ней не рисуем и ночью не зажигаем. */
let signBox: Rect | null = null;
const underSign = (x: number, y: number, w: number, h: number) =>
  !!signBox && x < signBox.x + signBox.w && x + w > signBox.x && y < signBox.y + signBox.h && y + h > signBox.y;

function windowAt(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, frame: string, windows: StaticLayer['windows'], warm: boolean) {
  if (underSign(x - 1, y - 1, w + 2, h + 2)) return;
  px(ctx, x - 1, y - 1, w + 2, h + 2, frame);
  px(ctx, x, y, w, h, C.glass);
  px(ctx, x, y, w, 2, shade(C.glass, 0.35));
  px(ctx, x + Math.floor(w / 2), y, 1, h, frame);
  px(ctx, x - 1, y + h + 1, w + 2, 1, shade(frame, -0.25));
  windows.push({ x, y, w, h, warm });
}

function sign(ctx: CanvasRenderingContext2D, label: string, cx: number, y: number, bg = '#2a2a35', fg = '#f4f1ea') {
  if (!label) return;
  const w = textWidth(label) + 6;
  const x = Math.round(cx - w / 2);
  signBox = { x: x - 1, y: y - 1, w: w + 2, h: 11 };
  px(ctx, x, y, w, 9, C.ink);
  px(ctx, x + 1, y + 1, w - 2, 7, bg);
  drawPixelText(ctx, label, x + 3, y + 2, fg);
}

function drawBuilding(ctx: CanvasRenderingContext2D, b: Building, windows: StaticLayer['windows']) {
  const X = b.x * TILE;
  const Y = b.y * TILE;
  const W = b.w * TILE;
  const H = b.h * TILE;
  const facadeH = b.floors * TILE;
  const roofH = H - facadeH;
  const FY = Y + roofH;
  const groundY = FY + (b.floors - 1) * TILE;
  const doorX = X + b.door * TILE + 4;
  const frame = b.style === 'brick' ? C.frame : shade(b.wall, -0.35);
  const warmRate = (i: number) => hash2(b.x * 31 + i, b.y, 5) > 0.35;
  // место вывески считаем заранее, чтобы окна под ней не рисовались
  signBox = null;
  if (b.label && b.style !== 'castle') {
    const sw = textWidth(b.label) + 8;
    const sy = b.style === 'shop' || b.style === 'airport' || b.style === 'glass' ? FY + 3 : b.style === 'station' ? FY + 2 : groundY - 5 - (b.style === 'stone' ? 0 : 6);
    signBox = { x: Math.round(X + W / 2 - sw / 2) - 1, y: sy - 1, w: sw + 2, h: 11 };
  }

  // тень дома на землю
  ctx.fillStyle = 'rgba(20, 24, 40, 0.22)';
  ctx.fillRect(X + 3, Y + H, W - 1, 3);
  ctx.fillRect(X + W, Y + 4, 3, H - 1);

  if (b.style === 'castle') {
    drawCastle(ctx, b, windows);
    return;
  }

  // крыша
  if (b.style === 'station') {
    px(ctx, X, Y, W, roofH, shade(b.wall, -0.2));
    ctx.fillStyle = '#a9c3d6';
    for (let j = 0; j < roofH; j++) {
      const t = 1 - j / roofH;
      const half = Math.sqrt(Math.max(0, 1 - (1 - t) * (1 - t))) * (W / 2 - 2);
      ctx.fillRect(X + W / 2 - half, Y + roofH - 1 - j, half * 2, 1);
    }
    for (let i = X + 6; i < X + W - 4; i += 8) px(ctx, i, Y + 6, 1, roofH - 7, '#6f8796');
    px(ctx, X, Y + roofH - 3, W, 3, shade(b.wall, -0.35));
  } else if (b.style === 'airport' || b.style === 'glass') {
    px(ctx, X, Y, W, roofH, b.roof);
    px(ctx, X, Y, W, 2, shade(b.roof, 0.2));
    px(ctx, X, Y + roofH - 3, W, 3, shade(b.roof, -0.3));
    for (let i = 0; i < 3; i++) {
      const ux = X + 6 + i * 14;
      if (ux + 8 < X + W) px(ctx, ux, Y + 6, 8, 6, shade(b.roof, -0.15));
    }
    if (b.style === 'airport') {
      // диспетчерская вышка и хвост самолёта над крышей
      px(ctx, X + 4, Y + 2, 6, roofH - 4, '#e6e9ee');
      px(ctx, X + 2, Y + 1, 10, 6, '#3f5c7a');
      px(ctx, X + 3, Y + 2, 8, 3, '#9fd0f5');
      px(ctx, X + W - 22, Y + 4, 16, 6, '#f4f4f4');
      px(ctx, X + W - 12, Y - 2, 6, 10, '#d63b3b');
      px(ctx, X + W - 12, Y + 2, 6, 2, '#2f5fa8');
    }
  } else {
    roofTiles(ctx, X, Y, W, roofH, b.roof);
    if (b.style === 'brick') {
      px(ctx, X + W - 12, Y - 4, 6, 8, '#7a3a2c');
      px(ctx, X + W - 13, Y - 5, 8, 2, '#3a2a26');
    }
  }

  // фасад
  if (b.style === 'brick') brickWall(ctx, X, FY, W, facadeH, b.wall);
  else if (b.style === 'stone' || b.style === 'station') stoneWall(ctx, X, FY, W, facadeH, b.wall);
  else if (b.style === 'airport' || b.style === 'glass') {
    px(ctx, X, FY, W, facadeH, b.wall);
    for (let i = X; i < X + W; i += 8) px(ctx, i, FY, 1, facadeH, shade(b.wall, -0.3));
    for (let j = FY; j < FY + facadeH; j += 8) px(ctx, X, j, W, 1, shade(b.wall, -0.3));
    for (let k = 0; k < W; k += 14) px(ctx, X + k + 3, FY + 2, 2, facadeH - 4, shade(b.wall, 0.35));
  } else {
    px(ctx, X, FY, W, facadeH, b.wall);
    for (let j = 0; j < facadeH; j++) for (let i = 0; i < W; i++) if (hash2(X + i, FY + j, 9) < 0.03) px(ctx, X + i, FY + j, 1, 1, shade(b.wall, -0.06));
  }
  px(ctx, X, FY, W, 1, shade(b.wall, -0.3));

  // окна верхних этажей
  if (b.style !== 'airport' && b.style !== 'glass') {
    let n = 0;
    for (let r = 0; r < b.floors - 1; r++) {
      const count = Math.max(1, Math.floor((W - 6) / 13));
      const gap = (W - count * 7) / (count + 1);
      for (let i = 0; i < count; i++) {
        const wx = Math.round(X + gap + i * (7 + gap));
        windowAt(ctx, wx, FY + r * TILE + 4, 7, 8, frame, windows, warmRate(n++));
      }
    }
  } else {
    for (let r = 0; r < b.floors - 1; r++) for (let i = 0; i < b.w * 2; i++) {
      const win = { x: X + i * 8 + 1, y: FY + r * TILE + 2, w: 6, h: 5, warm: warmRate(i + r * 10) };
      if (!underSign(win.x, win.y, win.w, win.h)) windows.push(win);
    }
  }

  // колонны и фронтон музея
  if (b.style === 'stone') {
    const top = FY + 2;
    for (let i = X + 4; i < X + W - 4; i += 10) {
      px(ctx, i, top, 5, facadeH - 6, '#f4efe4');
      px(ctx, i + 4, top, 1, facadeH - 6, '#c9c0ad');
      px(ctx, i - 1, top - 1, 7, 2, '#f8f5ee');
    }
    ctx.fillStyle = '#efe8da';
    for (let j = 0; j < 12; j++) ctx.fillRect(X + W / 2 - (j * W) / 24, FY - 12 + j, (j * W) / 12, 1);
    px(ctx, X, FY - 1, W, 2, '#cfc6b3');
    px(ctx, X - 2, Y + H - 3, W + 4, 3, '#efe8da');
    px(ctx, X - 2, Y + H - 3, W + 4, 1, '#fffaf0');
  }

  // первый этаж: витрина и маркиза или окна, дверь, вывеска
  if (b.style === 'shop' && b.awning) {
    const displayX = b.door === 1 ? X + 2 * TILE - 2 : X + 3;
    const displayW = W - TILE - 6;
    px(ctx, displayX, groundY + 5, displayW, 9, '#2a2a35');
    px(ctx, displayX + 1, groundY + 6, displayW - 2, 7, C.glass);
    px(ctx, displayX + 1, groundY + 6, displayW - 2, 2, shade(C.glass, 0.35));
    windows.push({ x: displayX + 1, y: groundY + 6, w: displayW - 2, h: 7, warm: true });
    for (let i = 0; i < W + 4; i++) px(ctx, X - 2 + i, groundY - 1, 1, 5, Math.floor(i / 3) % 2 ? b.awning[1] : b.awning[0]);
    for (let i = 0; i < W + 4; i += 3) px(ctx, X - 2 + i, groundY + 4, 2, 1, b.awning[0]);
    sign(ctx, b.label, X + W / 2, FY + 3, b.awning[0], '#ffffff');
    if (b.sign === 'cross') {
      const cx = X + W - 7;
      px(ctx, cx - 1, FY + 2, 3, 9, '#2fbf71');
      px(ctx, cx - 4, FY + 5, 9, 3, '#2fbf71');
    }
  } else if (b.style === 'station') {
    px(ctx, doorX - 8, groundY + 2, 24, 14, '#2a2a35');
    for (let j = 0; j < 6; j++) px(ctx, doorX - 8 + j, groundY + 2 + (6 - j), 24 - j * 2, 1, b.wall);
    px(ctx, doorX - 6, groundY + 6, 20, 10, '#f2c14e');
    windows.push({ x: doorX - 6, y: groundY + 6, w: 20, h: 10, warm: true });
    sign(ctx, b.label, X + W / 2, FY + 2);
    px(ctx, X + W / 2 - 4, Y + roofH - 12, 8, 8, '#f4f1ea');
    px(ctx, X + W / 2 - 1, Y + roofH - 11, 1, 4, C.ink);
    px(ctx, X + W / 2 - 1, Y + roofH - 8, 3, 1, C.ink);
  } else {
    for (let i = 0; i < b.w; i++) {
      if (i === b.door || b.style === 'airport' || b.style === 'glass') continue;
      windowAt(ctx, X + i * TILE + 4, groundY + 4, 8, 8, frame, windows, warmRate(i + 50));
    }
    if (b.style === 'airport') {
      sign(ctx, b.label, X + W / 2, FY + 3, '#1f2a44', '#f2c14e');
    } else if (b.style === 'glass') {
      sign(ctx, b.label, X + W / 2, FY + 3, '#1f2a44');
    } else if (b.label) {
      sign(ctx, b.label, X + W / 2, groundY - 5 - (b.style === 'stone' ? 0 : 6));
    }
  }
  // дверь
  if (b.style !== 'station') {
    const dark = b.style === 'airport' || b.style === 'glass' ? '#2c3e57' : C.door;
    px(ctx, doorX - 1, groundY + 2, 10, 14, C.ink);
    px(ctx, doorX, groundY + 3, 8, 13, dark);
    px(ctx, doorX + 1, groundY + 4, 6, 5, b.style === 'airport' || b.style === 'glass' ? C.glass : shade(dark, 0.2));
    px(ctx, doorX + 6, groundY + 10, 1, 1, '#f2c14e');
    px(ctx, doorX - 2, Y + H - 1, 12, 1, shade(C.pave, -0.2));
  }
}

function drawCastle(ctx: CanvasRenderingContext2D, b: Building, windows: StaticLayer['windows']) {
  const X = b.x * TILE;
  const Y = b.y * TILE;
  const W = b.w * TILE;
  const H = b.h * TILE;
  const facadeH = b.floors * TILE;
  const FY = Y + H - facadeH;
  // двор и стены
  stoneWall(ctx, X, Y, W, H - facadeH, shade(b.wall, -0.1));
  stoneWall(ctx, X, FY, W, facadeH, b.wall);
  px(ctx, X, FY, W, 2, shade(b.wall, 0.25));
  for (let i = X; i < X + W; i += 6) px(ctx, i, FY - 4, 4, 4, b.wall);
  // главная башня (Белая) с флагом
  const KX = X + W / 2 - 22;
  const KY = Y + 6;
  stoneWall(ctx, KX, KY, 44, H - facadeH - 4, '#e3e0d8');
  for (let i = KX; i < KX + 44; i += 6) px(ctx, i, KY - 3, 4, 3, '#e3e0d8');
  for (const [tx, ty] of [
    [KX - 3, KY - 3],
    [KX + 37, KY - 3],
  ]) {
    px(ctx, tx, ty, 10, 12, '#d9d5cc');
    px(ctx, tx + 1, ty - 4, 8, 4, '#6f7a8a');
    px(ctx, tx + 3, ty - 6, 4, 2, '#6f7a8a');
  }
  px(ctx, KX + 21, KY - 18, 1, 16, '#3a3a45');
  px(ctx, KX + 22, KY - 18, 11, 7, '#7c5cd6');
  drawPixelText(ctx, '?', KX + 26, KY - 17, '#f2c14e');
  for (let i = 0; i < 4; i++) {
    const wx = X + 10 + i * 34;
    if (wx + 3 > X + W - 4) continue;
    px(ctx, wx, FY + 8, 3, 8, C.ink);
    windows.push({ x: wx, y: FY + 8, w: 3, h: 8, warm: false });
  }
  // ворота с решёткой
  const gx = X + b.door * TILE - 2;
  px(ctx, gx - 1, FY + 14, 22, H - (FY - Y) - 14, C.ink);
  px(ctx, gx, FY + 17, 20, H - (FY - Y) - 17, '#2b2235');
  for (let j = 0; j < 4; j++) px(ctx, gx + j, FY + 14 + (4 - j), 20 - j * 2, 1, b.wall);
  for (let i = gx + 2; i < gx + 20; i += 4) px(ctx, i, FY + 17, 1, H - (FY - Y) - 17, '#6b6475');
  for (let j = FY + 20; j < Y + H; j += 4) px(ctx, gx, j, 20, 1, '#6b6475');
}

export function buildStatic(): StaticLayer {
  const ground = buildGround();
  const canvas = document.createElement('canvas');
  canvas.width = MAP_W * TILE;
  canvas.height = MAP_H * TILE;
  const ctx = canvas.getContext('2d')!;
  for (let ty = 0; ty < MAP_H; ty++) for (let tx = 0; tx < MAP_W; tx++) drawTile(ctx, ground, tx, ty);
  for (const d of DECOR) {
    if (d.kind === 'flowers') drawFlowers(ctx, d);
    if (d.kind === 'bench') drawBench(ctx, d);
  }
  const windows: StaticLayer['windows'] = [];
  for (const b of BUILDINGS) drawBuilding(ctx, b, windows);
  return { canvas, windows, ground };
}

function drawFlowers(ctx: CanvasRenderingContext2D, d: Decor) {
  const colors = ['#f25c78', '#f2c14e', '#ffffff', '#b57cf2'];
  for (let k = 0; k < 9; k++) {
    const x = d.x * TILE + 2 + Math.floor(hash2(d.x, k, 1) * 12);
    const y = d.y * TILE + 2 + Math.floor(hash2(k, d.y, 2) * 12);
    px(ctx, x, y + 1, 1, 1, C.grassDark);
    px(ctx, x, y, 1, 1, colors[k % colors.length]);
  }
}

function drawBench(ctx: CanvasRenderingContext2D, d: Decor) {
  const X = d.x * TILE;
  const Y = d.y * TILE;
  px(ctx, X + 1, Y + 6, 14, 3, '#8a5a3c');
  px(ctx, X + 1, Y + 10, 14, 2, '#a06b48');
  px(ctx, X + 2, Y + 12, 1, 3, '#3a2a26');
  px(ctx, X + 13, Y + 12, 1, 3, '#3a2a26');
}

// ---------------------------------------------------------------------------------------------- предметы (по y)
export function drawDecor(ctx: CanvasRenderingContext2D, d: Decor, night: boolean) {
  const X = d.x * TILE;
  const Y = d.y * TILE;
  if (d.kind === 'tree') {
    px(ctx, X + 6, Y + 9, 4, 6, '#6b4a33');
    ctx.fillStyle = 'rgba(20, 24, 40, 0.25)';
    ctx.fillRect(X + 2, Y + 14, 12, 2);
    const canopy = (cx: number, cy: number, r: number, c: string) => {
      ctx.fillStyle = c;
      for (let j = -r; j <= r; j++) {
        const half = Math.floor(Math.sqrt(r * r - j * j));
        ctx.fillRect(X + cx - half, Y + cy + j, half * 2 + 1, 1);
      }
    };
    canopy(8, 3, 9, C.ink);
    canopy(8, 3, 8, '#24612e');
    canopy(7, 1, 7, '#2f7d3a');
    canopy(5, -1, 4, '#4ea24f');
    px(ctx, X + 3, Y - 3, 2, 1, '#7cc76a');
  }
  if (d.kind === 'lamp') {
    px(ctx, X + 7, Y - 6, 2, 21, '#2a2f3a');
    px(ctx, X + 5, Y + 13, 6, 2, '#2a2f3a');
    px(ctx, X + 5, Y - 10, 6, 5, '#2a2f3a');
    px(ctx, X + 6, Y - 9, 4, 3, night ? '#ffe9a8' : '#d9e6ee');
  }
  if (d.kind === 'phonebox') {
    px(ctx, X + 3, Y - 10, 10, 25, C.ink);
    px(ctx, X + 4, Y - 9, 8, 23, '#d0312d');
    px(ctx, X + 4, Y - 9, 8, 2, '#a32420');
    for (let j = 0; j < 3; j++) px(ctx, X + 5, Y - 5 + j * 5, 6, 3, night ? '#ffe9a8' : '#f4efe4');
  }
  if (d.kind === 'postbox') {
    px(ctx, X + 4, Y + 1, 8, 14, C.ink);
    px(ctx, X + 5, Y + 2, 6, 12, '#d0312d');
    px(ctx, X + 5, Y + 2, 6, 2, '#a32420');
    px(ctx, X + 6, Y + 5, 4, 1, C.ink);
  }
  if (d.kind === 'bin') {
    px(ctx, X + 5, Y + 6, 6, 9, C.ink);
    px(ctx, X + 6, Y + 7, 4, 7, '#3c6e47');
  }
  if (d.kind === 'busstop') {
    px(ctx, X + 7, Y - 8, 2, 23, '#2a2f3a');
    px(ctx, X + 3, Y - 12, 10, 7, C.ink);
    px(ctx, X + 4, Y - 11, 8, 5, '#d0312d');
    px(ctx, X + 4, Y - 9, 8, 1, '#ffffff');
    px(ctx, X + 7, Y - 9, 2, 1, '#2f5fa8');
  }
  if (d.kind === 'bigben') {
    const BX = X;
    const base = Y + TILE;
    px(ctx, BX + 1, base - 96, 30, 96, C.ink);
    px(ctx, BX + 2, base - 95, 28, 94, '#d9c48f');
    for (let i = BX + 5; i < BX + 30; i += 5) px(ctx, i, base - 70, 1, 66, '#b9a26c');
    px(ctx, BX + 6, base - 84, 20, 20, C.ink);
    px(ctx, BX + 7, base - 83, 18, 18, night ? '#ffe9a8' : '#f4f1ea');
    px(ctx, BX + 15, base - 80, 2, 8, C.ink);
    px(ctx, BX + 15, base - 74, 6, 2, C.ink);
    for (let j = 0; j < 16; j++) px(ctx, BX + 4 + j, base - 96 - j, 24 - j * 2, 1, j % 3 ? '#4f6a5e' : '#3c5348');
  }
}

/** Красный двухэтажный автобус (3 клетки) и чёрное такси: едут по дороге на дневной карте. */
export function drawBus(ctx: CanvasRenderingContext2D, x: number, y: number, flip: boolean) {
  const X = Math.round(x);
  const Y = Math.round(y);
  ctx.save();
  if (flip) {
    ctx.translate(X * 2 + 48, 0);
    ctx.scale(-1, 1);
  }
  px(ctx, X, Y - 10, 48, 24, C.ink);
  px(ctx, X + 1, Y - 9, 46, 22, '#d0312d');
  px(ctx, X + 1, Y - 9, 46, 2, '#e85550');
  for (let i = 0; i < 6; i++) {
    px(ctx, X + 4 + i * 7, Y - 6, 5, 4, '#bfe0f5');
    px(ctx, X + 4 + i * 7, Y + 2, 5, 4, '#bfe0f5');
  }
  px(ctx, X + 42, Y + 1, 4, 8, '#bfe0f5');
  px(ctx, X + 1, Y - 1, 46, 1, '#a32420');
  px(ctx, X + 8, Y + 12, 7, 4, C.ink);
  px(ctx, X + 34, Y + 12, 7, 4, C.ink);
  ctx.restore();
}

export function drawCab(ctx: CanvasRenderingContext2D, x: number, y: number, flip: boolean) {
  const X = Math.round(x);
  const Y = Math.round(y);
  ctx.save();
  if (flip) {
    ctx.translate(X * 2 + 28, 0);
    ctx.scale(-1, 1);
  }
  px(ctx, X, Y + 2, 28, 11, C.ink);
  px(ctx, X + 1, Y + 3, 26, 9, '#23232b');
  px(ctx, X + 6, Y - 3, 14, 6, C.ink);
  px(ctx, X + 7, Y - 2, 12, 5, '#23232b');
  px(ctx, X + 8, Y - 1, 4, 3, '#9fc6e6');
  px(ctx, X + 14, Y - 1, 4, 3, '#9fc6e6');
  px(ctx, X + 11, Y - 5, 4, 2, '#f2c14e');
  px(ctx, X + 4, Y + 12, 5, 3, C.ink);
  px(ctx, X + 19, Y + 12, 5, 3, C.ink);
  ctx.restore();
}

/** Поезд Elizabeth line у перрона (место «поезд» в истории). */
export function drawTrain(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const X = Math.round(x);
  const Y = Math.round(y);
  px(ctx, X, Y - 6, 96, 18, C.ink);
  px(ctx, X + 1, Y - 5, 94, 16, '#e9e7ef');
  px(ctx, X + 1, Y + 6, 94, 3, '#6a3fa0');
  for (let i = 0; i < 12; i++) px(ctx, X + 4 + i * 7.6, Y - 2, 5, 5, '#2f3a52');
  px(ctx, X + 88, Y - 4, 6, 8, '#2f3a52');
}
